import { useState, useReducer, useRef, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import * as Action from "../../../../../store/redux/items_reducer.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import "./addNewItems.css";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// MUI Icons
import ArrowForward from "@mui/icons-material/ArrowForward";

import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AutoFixHighSharpIcon from "@mui/icons-material/AutoFixHighSharp";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AutoFixHighSharp from "@mui/icons-material/AutoFixHighSharp";
import BookmarkAddOutlined from "@mui/icons-material/BookmarkAddOutlined";
import BrandingWatermarkOutlined from "@mui/icons-material/BrandingWatermarkOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle"; 
import Close from "@mui/icons-material/Close"; 
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import CurrencyExchangeOutlined from "@mui/icons-material/CurrencyExchangeOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import Discount from "@mui/icons-material/Discount";
import ErrorOutlined from "@mui/icons-material/ErrorOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FilterTiltShift from "@mui/icons-material/FilterTiltShift";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import InventoryOutlined from "@mui/icons-material/InventoryOutlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import Numbers from "@mui/icons-material/Numbers";
import PhotoLibraryOutlined from "@mui/icons-material/PhotoLibraryOutlined";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import Save from "@mui/icons-material/Save";
import SellOutlined from "@mui/icons-material/SellOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import StraightenOutlined from "@mui/icons-material/StraightenOutlined";
import TrendingUp from "@mui/icons-material/TrendingUp";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";
import {
  productUnits,
  productStatuses,
} from "../../../../../unitsOfItems";

import IsLoading from "../../../../../IsLoading";
import AlertBar from "../../../../../AlertBar.jsx";


/* =========================================================
   SMALL REUSABLE UI COMPONENTS
========================================================= */

function SaaSSection({
  icon,
  eyebrow,
  title,
  description,
  badge,
  children,
  className = "",
}) {
  return (
    <section className={`ui-section-card ${className}`}>
      <div className="ui-section-card__header">
        <div className="ui-section-card__identity">

          <div className="ui-section-card__icon">
            {icon}
          </div>

          <div className="ui-section-card__heading">
            {eyebrow && (
              <span className="ui-eyebrow">
                {eyebrow}
              </span>
            )}

            <h2>{title}</h2>

            {description && (
              <p>{description}</p>
            )}
          </div>
        </div>

        {badge && (
          <div className="ui-section-badge">
            {badge}
          </div>
        )}
      </div>

      <div className="ui-section-card__body">
        {children}
      </div>
    </section>
  );
}


function Field({
  label,
  required,
  hint,
  error,
  children,
  full = false,
}) {
  return (
    <div className={`ui-field ${full ? "ui-field--full" : ""}`}>

      <div className="ui-field__label-row">

        <label>
          {label}
          {required && (
            <span className="ui-required">*</span>
          )}
        </label>

        {hint && (
          <span className="ui-field__hint">
            {hint}
          </span>
        )}
      </div>

      {children}

      {error && (
        <span className="ui-field__error">
          <ErrorOutlined />
          {error}
        </span>
      )}
    </div>
  );
}


function InputShell({
  icon,
  children,
  readonly = false,
  error = false,
}) {
  return (
    <div
      className={[
        "ui-input-shell",
        readonly ? "ui-input-shell--readonly" : "",
        error ? "ui-input-shell--error" : "",
      ].join(" ")}
    >
      {icon && (
        <span className="ui-input-shell__icon">
          {icon}
        </span>
      )}

      {children}
    </div>
  );
}


function MiniMetric({
  icon,
  label,
  value,
  tone = "default",
}) {
  return (
    <div className={`ui-mini-metric ui-mini-metric--${tone}`}>
      <div className="ui-mini-metric__icon">
        {icon}
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function AddNewItem({ onCancel }) {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [isAddingVariant, setIsAddingVariant] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [alert, setAlert] = useState(false);
  const [errors, setErrors] = useState({});

  const [inventoryData, setInventoryData] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);
  const [branchData, setBranchData] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    information: true,
    pricing: true,
    media: true,
    category: true,
    inventory: true,
    variants: true,
  });

  const {
    itemType,
    createVariantsPayloads,
    ItemVariants,
    createCategoriesPayloads,
  } = useSelector(
    (state) => state.itemsActions.addNewItem
  );

  const companyData = useSelector(
    (state) =>
      state.clientFunction?.companiesDB?.clientData
  );

  /* =========================================================
     COMPANY SKU PREFIX
  ========================================================= */

  const A =
    companyData?.founder?.firstName?.slice(0, 1);

  const B =
    companyData?.founder?.surName?.slice(0, 1);

  const C =
    companyData?.businessProfile?.basicInfo?.industry?.name?.slice(
      0,
      1
    );

  const D =
    companyData?.businessProfile?.basicInfo?.businessName?.slice(
      0,
      1
    );

  const E =
    companyData?.businessProfile?.basicInfo?.industryCategory?.slice(
      0,
      1
    );

  const F =
    companyData?.founder?.firstName?.slice(1, 2);

  const generatedSku = `${A || "P"}${B || "O"}${C || "U"}${D || "S"}${E || "I"}${F || "G"}`;


  /* =========================================================
     PRODUCT STATE
  ========================================================= */

  const [productPayloads, setProductPayloads] = useReducer(
    (state, action) => ({
      ...state,
      ...action,
    }),
    {
      itemType: "",
      barcode: "",
      qrcode: "",
      name: "",
      brand: "",
      assignedTo: "",
      assignedToId: "",

      unitsBaseUnit: "",
      unitsPurchaseUnit: "",
      unitsConversionRate: null,
      unitsSalesUnit: "",

      description: "",
      categoryId: "",
      supplierId: "",

      pricingCostPrice: null,
      pricingSellingPrice: null,
      pricingTaxRate: null,
      pricingCurrency: "NGN",

      stockReorderLevel: null,
      stockReorderQuantity: null,
      stockMinLevel: null,
      stockSellingQuantity: 1,

      inventoryLocation: "Main",
      inventoryQuantity: null,
      inventoryReservedQuantity: null,
      inventoryDamagedQuantity: null,

      warehousesWarehouseId: "",
      warehousesQuantity: 1,
      warehousesReservedQuantity: null,
      warehousesDamagedQuantity: null,

      batchBatchTracking: false,
      batchExpiryTracking: true,
      batchTrackingMethod: "FEFO",

      batchBatchNo: "",
      batchCostPrice: null,
      batchQuantityAvailable: null,
      batchManufactureDate: "",
      batchExpiryDate: "",
      batchWarehouseId: "",

      unitsOfMeasure: null,
      dimensionsWeight: null,
      dimensionsLength: null,
      dimensionsWidth: null,
      dimensionsHeight: null,

      images: [],

      trackInventory: true,
      status: "active",
    }
  );


  /* =========================================================
     PAYLOAD
     Backend structure intentionally retained.
  ========================================================= */

  const payload = useMemo(
    () => ({
      clientId: companyData?.clientId,

      itemType: productPayloads.itemType,

      sku: generatedSku,

      barcode: productPayloads.barcode,

      qrcode: productPayloads.qrcode,

      name: productPayloads.name,

      brand: productPayloads.brand,

      assignedTo: productPayloads.assignedTo,

      units: {
        baseUnit:
          productPayloads.unitsBaseUnit,

        purchaseUnit:
          productPayloads.unitsPurchaseUnit,

        conversionRate:
          productPayloads.unitsConversionRate,

        salesUnit:
          productPayloads.unitsSalesUnit,
      },

      description:
        productPayloads.description,

      categoryId:
        productPayloads.categoryId,

      supplierId:
        productPayloads.supplierId,

      ...(productPayloads.assignedTo ===
        "INVENTORY_ITEM" && {
        inventories: [
          {
            inventoryId:
              productPayloads.assignedToId,

            location:
              productPayloads.inventoryLocation,

            stock: {
              quantity:
                productPayloads.inventoryQuantity,

              reservedQuantity:
                productPayloads.inventoryReservedQuantity,

              damagedQuantity:
                productPayloads.inventoryDamagedQuantity,

              reorderLevel:
                productPayloads.stockReorderLevel,

              reorderQuantity:
                productPayloads.stockReorderQuantity,

              minLevel:
                productPayloads.stockMinLevel,

              sellingQuantity:
                productPayloads.stockSellingQuantity,
            },

            batchTracking: {
              batchTracking:
                productPayloads.batchBatchTracking,

              expiryTracking:
                productPayloads.batchExpiryTracking,

              trackingMethod:
                productPayloads.batchTrackingMethod,

              batches: [
                {
                  batchNo:
                    productPayloads.batchBatchNo,

                  costPrice:
                    productPayloads.batchCostPrice,

                  quantityAvailable:
                    productPayloads.batchQuantityAvailable,

                  manufactureDate:
                    productPayloads.batchManufactureDate,

                  expiryDate:
                    productPayloads.batchExpiryDate,
                },
              ],
            },
          },
        ],
      }),

      ...(productPayloads.assignedTo ===
        "WAREHOUSE_ITEM" && {
        warehouses: [
          {
            warehouseId:
              productPayloads.assignedToId,

            quantity:
              productPayloads.warehousesQuantity,

            reservedQuantity:
              productPayloads.warehousesReservedQuantity,

            damagedQuantity:
              productPayloads.warehousesDamagedQuantity,

            batchTracking: {
              batchTracking:
                productPayloads.batchBatchTracking,

              expiryTracking:
                productPayloads.batchExpiryTracking,

              trackingMethod:
                productPayloads.batchTrackingMethod,

              batches: [
                {
                  batchNo:
                    productPayloads.batchBatchNo,

                  costPrice:
                    productPayloads.batchCostPrice,

                  quantityAvailable:
                    productPayloads.batchQuantityAvailable,

                  manufactureDate:
                    productPayloads.batchManufactureDate,

                  expiryDate:
                    productPayloads.batchExpiryDate,
                },
              ],
            },
          },
        ],
      }),

      pricing: {
        costPrice:
          productPayloads.pricingCostPrice,

        sellingPrice:
          productPayloads.pricingSellingPrice,

        taxRate:
          productPayloads.pricingTaxRate,

        currency:
          productPayloads.pricingCurrency,
      },

      dimensions: {
        unitsOfMeasure:
          productPayloads.unitsOfMeasure,

        weight:
          productPayloads.dimensionsWeight,

        length:
          productPayloads.dimensionsLength,

        width:
          productPayloads.dimensionsWidth,

        height:
          productPayloads.dimensionsHeight,
      },

      itemCategory:
        createCategoriesPayloads,

      images:
        productPayloads.images,

      trackInventory:
        productPayloads.trackInventory,

      status:
        productPayloads.status,

      createdBy:
        companyData?.clientId,

      updatedBy: "",

      createdAt:
        new Date().toISOString(),
    }),
    [
      companyData?.clientId,
      productPayloads,
      createCategoriesPayloads,
      generatedSku,
    ]
  );


  /* =========================================================
     DERIVED UI VALUES
  ========================================================= */

  const sellingPrice =
    Number(
      productPayloads.pricingSellingPrice || 0
    );

  const costPrice =
    Number(
      productPayloads.pricingCostPrice || 0
    );

  const estimatedProfit =
    sellingPrice - costPrice;

  const margin =
    sellingPrice > 0
      ? (estimatedProfit / sellingPrice) * 100
      : 0;


  const completionFields = [
    productPayloads.name,
    productPayloads.brand,
    productPayloads.itemType,
    productPayloads.pricingSellingPrice,
    productPayloads.pricingCostPrice,
    productPayloads.unitsSalesUnit,
    productPayloads.assignedTo,
    productPayloads.stockMinLevel,
  ];

  const completion =
    Math.min(
      100,
      Math.round(
        (completionFields.filter(Boolean).length /
          completionFields.length) *
          100
      )
    );


  const toggleSection = (section) => {
    setExpandedSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  };


  /* =========================================================
     FETCH COMPANY DEFAULTS
  ========================================================= */

  useEffect(() => {
    if (!companyData?.clientId) return;

    let mounted = true;

    const fetchCompanyDefaults = async () => {
      try {
        const BASE_URL =
          process.env.REACT_APP_SERVER_SCRIPT_HOST;

        setLoading(true);

        const [
          branchResponse,
          inventoryResponse,
          warehouseResponse,
        ] = await Promise.all([
          axios.get(
            `${BASE_URL}/company-defualt/branches-payload/fetch-branch/${companyData.clientId}`
          ),

          axios.get(
            `${BASE_URL}/company-defualt/inventories-payload/fetch-inventory/${companyData.clientId}`
          ),

          axios.get(
            `${BASE_URL}/company-defualt/warehouses-payload/fetch-warehouse/${companyData.clientId}`
          ),
        ]);

        if (!mounted) return;

        const branches =
          branchResponse?.data?.companyBranchData || [];

        const inventories =
          inventoryResponse?.data?.companyInventoryData || [];

        const warehouses =
          warehouseResponse?.data?.companyWarehouseData || [];

        setBranchData(branches);
        setInventoryData(inventories);
        setWarehouseData(warehouses);

        if (
          inventories?.length &&
          !productPayloads.inventoryLocation
        ) {
          setProductPayloads({
            inventoryLocation:
              inventories[0]?.location || "",
          });
        }
      } catch (error) {
        console.error(
          "Failed to fetch company defaults:",
          error?.code || "REQUEST_FAILED"
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanyDefaults();

    return () => {
      mounted = false;
    };
  }, [companyData?.clientId]);


  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateProductForm = () => {
    const newErrors = {};

    if (!productPayloads.name?.trim()) {
      newErrors.name =
        "Item title is required.";
    }

    if (!productPayloads.status?.trim()) {
      newErrors.status =
        "Please select an item status.";
    }

    if (!productPayloads.itemType?.trim()) {
      newErrors.itemType =
        "Please select an item type.";
    }

    if (!productPayloads.brand?.trim()) {
      newErrors.brand =
        "Brand is required.";
    }

    if (
      productPayloads.pricingSellingPrice === "" ||
      productPayloads.pricingSellingPrice === null ||
      Number(productPayloads.pricingSellingPrice) <= 0
    ) {
      newErrors.pricingSellingPrice =
        "Selling price must be greater than zero.";
    }

    if (
      productPayloads.pricingCostPrice === "" ||
      productPayloads.pricingCostPrice === null ||
      Number(productPayloads.pricingCostPrice) <= 0
    ) {
      newErrors.pricingCostPrice =
        "Cost price must be greater than zero.";
    }

    if (!productPayloads.assignedTo?.trim()) {
      newErrors.assignedTo =
        "Please select where this item will be stored.";
    }

    if (!productPayloads.unitsSalesUnit?.trim()) {
      newErrors.unitsSalesUnit =
        "Please select a sales unit.";
    }

    if (
      productPayloads.stockMinLevel === null ||
      productPayloads.stockMinLevel === undefined ||
      Number.isNaN(
        Number(productPayloads.stockMinLevel)
      ) ||
      Number(productPayloads.stockMinLevel) < 0
    ) {
      newErrors.stockMinLevel =
        "Enter a valid minimum stock level.";
    }

    if (
      productPayloads.assignedTo ===
        "INVENTORY_ITEM" &&
      !productPayloads.assignedToId
    ) {
      newErrors.assignedToId =
        "Please select an inventory.";
    }

    if (
      productPayloads.assignedTo ===
        "INVENTORY_ITEM" &&
      (
        productPayloads.inventoryQuantity === null ||
        productPayloads.inventoryQuantity === "" ||
        Number(productPayloads.inventoryQuantity) < 0
      )
    ) {
      newErrors.inventoryQuantity =
        "Enter a valid inventory quantity.";
    }

    if (
      productPayloads.assignedTo ===
        "WAREHOUSE_ITEM" &&
      !productPayloads.assignedToId
    ) {
      newErrors.assignedToId =
        "Please select a warehouse.";
    }

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length === 0
    );
  };


  /* =========================================================
     POST PRODUCT
  ========================================================= */

  const apiPostProducts = async () => {
    setLoading(true);

    try {
      const BASE_URL =
        process.env.REACT_APP_SERVER_SCRIPT_HOST;

      const clientId =
        companyData?.clientId;

      if (!clientId) {
        throw new Error(
          "Client ID is missing."
        );
      }

      const { data } = await axios.post(
        `${BASE_URL}/client/${clientId}/manage_products/add_new_Item`,
        payload
      );

      if (data?.status === 201) {
        setAlert({
          message:
            data?.message ||
            "Your item was created successfully. All product details have been saved and the item is now ready for inventory and sales operations.",
          type: "added",
        });

        return data;
      }

      enqueueSnackbar(
        data?.message ||
          "Failed to upload product.",
        {
          variant: "error",
          autoHideDuration: 3000,
        }
      );

      return null;
    } catch (error) {
      console.error(
        "Add Product Error:",
        error?.code || "REQUEST_FAILED"
      );

      let message =
        "Unable to add product. Please try again.";

      if (
        error.code === "ERR_NETWORK"
      ) {
        message =
          "No internet connection. Please check your network and try again.";
      } else if (
        error.response?.data?.message
      ) {
        message =
          error.response.data.message;
      } else if (error.message) {
        message =
          error.message;
      }

      enqueueSnackbar(message, {
        variant: "error",
        autoHideDuration: 4000,
      });

      return null;
    } finally {
      setLoading(false);
    }
  };


  const handleAddNewItem = async () => {
    const isValid =
      validateProductForm();

    if (!isValid) {
      enqueueSnackbar(
        "Please complete the required fields before saving.",
        {
          variant: "error",
          autoHideDuration: 3000,
        }
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    const result =
      await apiPostProducts();

    if (result) {
      dispatch(
        Action.clearAllData()
      );
    }
  };


  /* =========================================================
     VARIANTS
  ========================================================= */

  const addVariants = () => {
    const variantPayload = {
      ...createVariantsPayloads,

      option:
        createVariantsPayloads.option?.trim(),

      values:
        createVariantsPayloads.values
          ?.map((value) =>
            value.trim()
          )
          .filter(Boolean) || [],
    };

    if (!variantPayload.option) {
      enqueueSnackbar(
        "Variant option name is required.",
        {
          variant: "warning",
        }
      );
      return;
    }

    if (
      variantPayload.values.length === 0
    ) {
      enqueueSnackbar(
        "Add at least one variant value.",
        {
          variant: "warning",
        }
      );
      return;
    }

    dispatch(
      Action.addVariants(
        variantPayload
      )
    );

    setIsAddingVariant(false);
  };


  /* =========================================================
     STATUS LABEL
  ========================================================= */

  const statusLabel = useMemo(() => {
    const value =
      productPayloads.status || "active";

    return String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) =>
        char.toUpperCase()
      );
  }, [productPayloads.status]);


  /* =========================================================
     RENDER
  ========================================================= */


  const fileInputRef = useRef(null);

const [isDraggingImage, setIsDraggingImage] = useState(false);
const [imagePreview, setImagePreview] = useState(null);

const MAX_IMAGES = 8;

const processImageFiles = useCallback((files) => {
  const incomingFiles = Array.from(files || []);

  if (!incomingFiles.length) return;

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  const validFiles = incomingFiles.filter((file) => {
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar(
        `${file.name} is not supported. Use JPG, PNG or WEBP.`,
        {
          variant: "warning",
          autoHideDuration: 3000,
        }
      );

      return false;
    }

    return true;
  });

  if (!validFiles.length) return;

  const currentImages = productPayloads.images || [];

  const remainingSlots = MAX_IMAGES - currentImages.length;

  if (remainingSlots <= 0) {
    enqueueSnackbar(
      `You can upload a maximum of ${MAX_IMAGES} images.`,
      {
        variant: "warning",
        autoHideDuration: 3000,
      }
    );

    return;
  }

  const filesToAdd = validFiles.slice(0, remainingSlots);

  const newImageUrls = filesToAdd.map((file) =>
    URL.createObjectURL(file)
  );

  setProductPayloads({
    images: [...currentImages, ...newImageUrls],
  });

  if (validFiles.length > remainingSlots) {
    enqueueSnackbar(
      `Only ${remainingSlots} more image${
        remainingSlots === 1 ? "" : "s"
      } can be added.`,
      {
        variant: "warning",
        autoHideDuration: 3000,
      }
    );
  }
}, [productPayloads.images, enqueueSnackbar]);


const handleBrowseImages = () => {
  fileInputRef.current?.click();
};


const handleImageInputChange = (event) => {
  const files = event.target.files;

  processImageFiles(files);

  // Allows selecting the same file again later.
  event.target.value = "";
};


const handleImageDragOver = (event) => {
  event.preventDefault();
  event.stopPropagation();

  setIsDraggingImage(true);
};


const handleImageDragLeave = (event) => {
  event.preventDefault();
  event.stopPropagation();

  setIsDraggingImage(false);
};


const handleImageDrop = (event) => {
  event.preventDefault();
  event.stopPropagation();

  setIsDraggingImage(false);

  const files = event.dataTransfer.files;

  processImageFiles(files);
};


const removeImage = (index) => {
  const currentImages = productPayloads.images || [];

  const imageToRemove = currentImages[index];

  // Release browser object URL.
  if (
    typeof imageToRemove === "string" &&
    imageToRemove.startsWith("blob:")
  ) {
    URL.revokeObjectURL(imageToRemove);
  }

  setProductPayloads({
    images: currentImages.filter((_, i) => i !== index),
  });

  if (imagePreview === imageToRemove) {
    setImagePreview(null);
  }
};


const openImagePreview = (image) => {
  setImagePreview(image);
};


const closeImagePreview = () => {
  setImagePreview(null);
};
  return (
    <>
      {loading && <IsLoading />}

      {alert && (
        <AlertBar
          alert={alert}
          setAlert={setAlert}
        />
      )}

      <div
        id="addNewItemcomp"
        className="uni-create-item"
      >

        {/* =====================================================
            TOP APPLICATION BAR
        ===================================================== */}

        <header className="uni-create-header">

          <div className="uni-create-header__left">

            <button
              type="button"
              className="uni-back-button"
              onClick={onCancel}
              aria-label="Close item creator"
            >
              <Close />
            </button>

            <div className="uni-product-mark">
              <Inventory2Outlined />
            </div>

            <div className="uni-create-heading">

              <div className="uni-create-heading__meta">
                <span>
                  INVENTORY
                </span>

                <i />

                <span>
                  NEW ITEM
                </span>

                <span className="uni-draft-pill">
                  <span />
                  Draft
                </span>
              </div>

              <h1>
                Create new item
              </h1>

              <p>
                Build a complete product profile
                for your inventory, purchasing and
                POS workflows.
              </p>

            </div>
          </div>


          <div className="uni-create-header__right">

            <div className="uni-autosave">

              <CheckCircle />

              <div>
                <span>
                  Workspace
                </span>

                <strong>
                  Auto-saved
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="uni-secondary-action"
              onClick={onCancel}
            >
              Cancel
            </button>

            <button
              type="button"
              className="uni-primary-action"
              onClick={handleAddNewItem}
            >
              <Save />
              Save item
              <ArrowForward />
            </button>

          </div>
        </header>


        {/* =====================================================
            CREATION PROGRESS
        ===================================================== */}

        <div className="uni-creation-progress">

          <div className="uni-progress-summary">

            <div>
              <span>
                ITEM SETUP
              </span>

              <strong>
                {productPayloads.name ||
                  "Untitled item"}
              </strong>
            </div>

            <div className="uni-completion-score">

              <div>
                <span>
                  Profile completion
                </span>

                <strong>
                  {completion}%
                </strong>
              </div>

              <div className="uni-completion-track">
                <span
                  style={{
                    width: `${completion}%`,
                  }}
                />
              </div>
            </div>
          </div>


          <div className="uni-progress-steps">

            <div className="uni-progress-step is-active">
              <div>
                <CheckCircle />
              </div>
              <span>
                Basic information
              </span>
            </div>

            <div
              className={`uni-progress-line ${
                productPayloads.pricingSellingPrice
                  ? "is-active"
                  : ""
              }`}
            />

            <div
              className={`uni-progress-step ${
                productPayloads.pricingSellingPrice
                  ? "is-active"
                  : ""
              }`}
            >
              <div>
                <CurrencyExchangeOutlined />
              </div>
              <span>
                Pricing
              </span>
            </div>

            <div
              className={`uni-progress-line ${
                productPayloads.assignedTo
                  ? "is-active"
                  : ""
              }`}
            />

            <div
              className={`uni-progress-step ${
                productPayloads.assignedTo
                  ? "is-active"
                  : ""
              }`}
            >
              <div>
                <InventoryOutlined />
              </div>
              <span>
                Inventory
              </span>
            </div>

            <div
              className={`uni-progress-line ${
                ItemVariants?.length
                  ? "is-active"
                  : ""
              }`}
            />

            <div
              className={`uni-progress-step ${
                ItemVariants?.length
                  ? "is-active"
                  : ""
              }`}
            >
              <div>
                <FilterTiltShift />
              </div>
              <span>
                Variants
              </span>
            </div>

          </div>
        </div>


        {/* =====================================================
            MAIN WORKSPACE
        ===================================================== */}

        <main className="uni-workspace">

          <div className="uni-main-column">

            {/* =================================================
                BASIC INFORMATION
            ================================================= */}

            <SaaSSection
              icon={<DescriptionOutlined />}
              eyebrow="CORE INFORMATION"
              title="Item information"
              description="Give this item a clear identity across your inventory, purchasing and point-of-sale workflows."
              badge={
                <>
                  <span className="uni-badge-dot" />
                  Required
                </>
              }
            >

              <div className="uni-form-grid">

                <Field
                  label="Item title"
                  required
                  full
                  error={errors.name}
                  hint="Customer-facing product name"
                >
                  <InputShell
                    icon={<Inventory2Outlined />}
                    error={Boolean(errors.name)}
                  >
                    <input
                      type="text"
                      value={
                        productPayloads.name
                      }
                      placeholder="e.g. Peak Milk 400g"
                      onChange={(e) => {
                        setProductPayloads({
                          name: e.target.value,
                        });

                        setErrors((prev) => ({
                          ...prev,
                          name: "",
                        }));
                      }}
                    />

                    <span className="ui-input-status">
                      {productPayloads.name
                        ? "Ready"
                        : "Required"}
                    </span>
                  </InputShell>
                </Field>


                <Field
                  label="Brand"
                  required
                  error={errors.brand}
                >
                  <InputShell
                    icon={
                      <BrandingWatermarkOutlined />
                    }
                    error={Boolean(errors.brand)}
                  >
                    <input
                      type="text"
                      value={
                        productPayloads.brand
                      }
                      placeholder="Enter brand"
                      onChange={(e) => {
                        setProductPayloads({
                          brand: e.target.value,
                        });

                        setErrors((prev) => ({
                          ...prev,
                          brand: "",
                        }));
                      }}
                    />
                  </InputShell>
                </Field>


                <Field
                  label="Generated SKU"
                  hint="Automatically generated"
                >
                  <InputShell
                    icon={<Numbers />}
                    readonly
                  >
                    <input
                      value={generatedSku}
                      readOnly
                    />

                    <span className="ui-auto-tag">
                      AUTO
                    </span>
                  </InputShell>
                </Field>


                <Field
                  label="Description"
                  full
                  hint="Rich product description"
                >
                  <div className="uni-editor-shell">
                    <ReactQuill
                      className="uni-product-editor"
                      theme="snow"
                      value={
                        productPayloads.description
                      }
                      onChange={(description) =>
                        setProductPayloads({
                          description,
                        })
                      }
                    />
                  </div>
                </Field>

              </div>

            </SaaSSection>


            {/* =================================================
                PRICING
            ================================================= */}

            <SaaSSection
              icon={<CurrencyExchangeOutlined />}
              eyebrow="FINANCIAL CONFIGURATION"
              title="Pricing & profitability"
              description="Configure your commercial pricing and understand your expected margin before saving."
              badge={
                <span className="uni-smart-badge">
                  <AutoFixHighSharp />
                  Item pricing
                </span>
              }
            >

              <div className="uni-pricing-grid">

                <Field
                  label="Selling price"
                  required
                  error={
                    errors.pricingSellingPrice
                  }
                  hint="Customer selling price"
                >
                  <div
                    className={`uni-money-input ${
                      errors.pricingSellingPrice
                        ? "is-error"
                        : ""
                    }`}
                  >
                    <span className="uni-money-symbol">
                      ₦
                    </span>

                    <input
                      type="number"
                      min="0"
                      value={
                        productPayloads.pricingSellingPrice ??
                        ""
                      }
                      placeholder="0.00"
                      onChange={(e) => {
                        setProductPayloads({
                          pricingSellingPrice:
                            e.target.value === ""
                              ? ""
                              : Number(
                                  e.target.value
                                ),
                        });

                        setErrors((prev) => ({
                          ...prev,
                          pricingSellingPrice:
                            "",
                        }));
                      }}
                    />

                    <span className="uni-money-currency">
                      NGN
                    </span>
                  </div>
                </Field>


                <Field
                  label="Cost price"
                  required
                  error={
                    errors.pricingCostPrice
                  }
                  hint="Your acquisition cost"
                >
                  <div
                    className={`uni-money-input ${
                      errors.pricingCostPrice
                        ? "is-error"
                        : ""
                    }`}
                  >
                    <span className="uni-money-symbol">
                      ₦
                    </span>

                    <input
                      type="number"
                      min="0"
                      value={
                        productPayloads.pricingCostPrice ??
                        ""
                      }
                      placeholder="0.00"
                      onChange={(e) => {
                        setProductPayloads({
                          pricingCostPrice:
                            e.target.value === ""
                              ? ""
                              : Number(
                                  e.target.value
                                ),
                        });

                        setErrors((prev) => ({
                          ...prev,
                          pricingCostPrice:
                            "",
                        }));
                      }}
                    />

                    <span className="uni-money-currency">
                      NGN
                    </span>
                  </div>
                </Field>

              </div>


              <div className="uni-profit-summary">

                <div className="uni-profit-summary__primary">

                  <div className="uni-profit-symbol">
                    <TrendingUp />
                  </div>

                  <div>
                    <span>
                      Estimated item profit
                    </span>

                    <strong>
                      ₦
                      {estimatedProfit.toLocaleString()}
                    </strong>
                  </div>
                </div>


                <div className="uni-profit-divider" />


                <div className="uni-profit-stat">
                  <span>
                    Gross margin
                  </span>

                  <strong>
                    {margin.toFixed(1)}%
                  </strong>
                </div>


                <div className="uni-profit-divider" />


                <div className="uni-profit-stat">
                  <span>
                    Currency
                  </span>

                  <strong>
                    NGN
                  </strong>
                </div>

              </div>

            </SaaSSection>


            {/* =================================================
                MEDIA
            ================================================= */}

            <SaaSSection
              icon={<PhotoLibraryOutlined />}
              eyebrow="PRODUCT MEDIA"
              title="Item images"
              description="Use clear product imagery to help your team identify items quickly."
              badge={
                <span className="uni-media-count">
                  {productPayloads.images?.length || 0}
                  <small>/8</small>
                </span>
              }
            >

            <section className="uni-card uni-media-card">

  {/* ================= MEDIA HEADER ================= */}

  <div className="uni-card-header">

    

    <div className="uni-media-counter">
      <strong>
        {productPayloads.images?.length || 0}
      </strong>
      <span>/ {MAX_IMAGES}</span>
    </div>

  </div>


  {/* ================= HIDDEN FILE INPUT ================= */}

  <input
    ref={fileInputRef}
    type="file"
    accept="image/jpeg,image/png,image/webp"
    multiple
    hidden
    onChange={handleImageInputChange}
  />


  {/* ================= UPLOAD WORKSPACE ================= */}

  <div className="uni-media-layout">

    {/* ================= DROPZONE ================= */}

    <div
      className={`uni-media-dropzone ${
        isDraggingImage
          ? "uni-media-dropzone--dragging"
          : ""
      } ${
        productPayloads.images?.length >= MAX_IMAGES
          ? "uni-media-dropzone--full"
          : ""
      }`}
      onDragOver={handleImageDragOver}
      onDragEnter={handleImageDragOver}
      onDragLeave={handleImageDragLeave}
      onDrop={handleImageDrop}
      onClick={
        productPayloads.images?.length < MAX_IMAGES
          ? handleBrowseImages
          : undefined
      }
    >

      <div className="uni-media-dropzone__glow" />


      <div className="uni-media-upload-icon">

        {isDraggingImage ? (
          <AddPhotoAlternateOutlinedIcon />
        ) : (
          <CloudUploadOutlinedIcon />
        )}

      </div>


      <h3>
        {isDraggingImage
          ? "Drop your images here"
          : productPayloads.images?.length >= MAX_IMAGES
          ? "Image limit reached"
          : "Drop item images here"}
      </h3>


      <p>
        {productPayloads.images?.length >= MAX_IMAGES
          ? `You've reached the ${MAX_IMAGES} image limit.`
          : "Drag and drop your product images or browse from your computer."}
      </p>


      {/* ================= ACTIONS ================= */}

      {productPayloads.images?.length < MAX_IMAGES && (
        <div
          className="uni-media-actions"
          onClick={(event) => event.stopPropagation()}
        >

          <button
            type="button"
            className="uni-media-primary"
            onClick={handleBrowseImages}
          >
            <CloudUploadOutlinedIcon />
            Browse files
          </button>


          <button
            type="button"
            className="uni-media-secondary"
            onClick={handleBrowseImages}
          >
            <PhotoLibraryOutlinedIcon />
            Choose images
          </button>

        </div>
      )}


      <span className="uni-media-hint">
        JPG, PNG or WEBP · Recommended 1200 × 1200px ·
        Maximum {MAX_IMAGES} images
      </span>

    </div>


    {/* ================= MEDIA TIPS ================= */}

    <div className="uni-media-tips">

      <div className="uni-media-tip">

        <div>
          <AutoFixHighSharpIcon />
        </div>

        <span>
          <strong>Better recognition</strong>

          <small>
            Clear product images help your team identify
            items faster.
          </small>
        </span>

      </div>


      <div className="uni-media-tip">

        <div>
          <Inventory2OutlinedIcon />
        </div>

        <span>
          <strong>POS ready</strong>

          <small>
            Your product imagery can be displayed throughout
            inventory and sales workflows.
          </small>
        </span>

      </div>


      <div className="uni-media-tip">

        <div>
          <CheckCircle />
        </div>

        <span>
          <strong>Multiple images</strong>

          <small>
            Add up to {MAX_IMAGES} images to give your team
            a complete view of the item.
          </small>
        </span>

      </div>

    </div>

  </div>


  {/* ================= IMAGE PREVIEW GRID ================= */}

  {productPayloads.images?.length > 0 && (

    <div className="uni-media-gallery">

      <div className="uni-media-gallery__header">

        <div>

          <span className="uni-card-eyebrow">
            UPLOADED MEDIA
          </span>

          <h3>
            Product gallery
          </h3>

        </div>

        <span>
          {productPayloads.images.length} image
          {productPayloads.images.length !== 1
            ? "s"
            : ""}
        </span>

      </div>


      <div className="uni-media-grid">

        {productPayloads.images.map((image, index) => (

          <div
            className={`uni-media-item ${
              index === 0
                ? "uni-media-item--primary"
                : ""
            }`}
            key={`${image}-${index}`}
          >

            {/* IMAGE */}

            <img
              src={image}
              alt={`${productPayloads.name || "Product"} ${
                index + 1
              }`}
              onClick={() => openImagePreview(image)}
            />


            {/* PRIMARY BADGE */}

            {index === 0 && (

              <span className="uni-media-primary-badge">
                <CheckCircle />
                Primary
              </span>

            )}


            {/* INDEX */}

            <span className="uni-media-index">
              {index + 1}
            </span>


            {/* ACTIONS */}

            <div className="uni-media-item-actions">

              <button
                type="button"
                title="Preview image"
                onClick={() =>
                  openImagePreview(image)
                }
              >
                <OpenInFullOutlinedIcon />
              </button>


              <button
                type="button"
                title="Remove image"
                onClick={() =>
                  removeImage(index)
                }
              >
                <DeleteOutlineOutlinedIcon />
              </button>

            </div>

          </div>

        ))}


        {/* ADD MORE */}

        {productPayloads.images.length < MAX_IMAGES && (

          <button
            type="button"
            className="uni-media-add-more"
            onClick={handleBrowseImages}
          >

            <AddPhotoAlternateOutlinedIcon />

            <strong>
              Add more
            </strong>

            <span>
              {MAX_IMAGES -
                productPayloads.images.length}{" "}
              slot
              {MAX_IMAGES -
                productPayloads.images.length !==
              1
                ? "s"
                : ""}{" "}
              remaining
            </span>

          </button>

        )}

      </div>

    </div>

  )}


  {/* ================= EMPTY STATE ================= */}

  {!productPayloads.images?.length && (

    <div className="uni-media-empty">

      <PhotoLibraryOutlinedIcon />

      <div>

        <strong>
          No product images yet
        </strong>

        <span>
          Add at least one image to create a more
          recognizable product experience.
        </span>

      </div>

    </div>

  )}

</section>
            </SaaSSection>


            {/* =================================================
                CATEGORY
            ================================================= */}

            <SaaSSection
              icon={<BookmarkAddOutlined />}
              eyebrow="CATALOG STRUCTURE"
              title="Category & metafields"
              description="Organize the item with flexible classification data."
              badge={
                createCategoriesPayloads?.length
                  ? `${createCategoriesPayloads.length} fields`
                  : "Optional"
              }
            >

              <div className="uni-category-container">

                {createCategoriesPayloads?.length >
                0 ? (
                  <div className="uni-category-tags">

                    {createCategoriesPayloads.map(
                      (category, index) => (
                        <div
                          className="uni-category-tag"
                          key={index}
                        >
                          <span>
                            {category}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              dispatch(
                                Action.removeFromCategory(
                                  index
                                )
                              )
                            }
                          >
                            <Close />
                          </button>
                        </div>
                      )
                    )}

                  </div>
                ) : (
                  <div className="uni-empty-category">
                    <BookmarkAddOutlined />

                    <div>
                      <strong>
                        No category metafields
                      </strong>

                      <span>
                        Add classification data to
                        improve search and discovery.
                      </span>
                    </div>
                  </div>
                )}


                <button
                  type="button"
                  className="uni-outline-action"
                  onClick={() =>
                    setIsAddingCategory(
                      !isAddingCategory
                    )
                  }
                >
                  <BookmarkAddOutlined />
                  Add category
                  <ExpandMore
                    className={
                      isAddingCategory
                        ? "rotate"
                        : ""
                    }
                  />
                </button>


                {isAddingCategory && (
                  <div className="uni-inline-editor">

                    <label>
                      Category values
                    </label>

                    <input
                      type="text"
                      placeholder="e.g. Dairy, Grocery, Beverages"
                      onChange={(e) =>
                        dispatch(
                          Action.setCreateCategoriesPayloads(
                            {
                              category:
                                e.target.value
                                  .split(",")
                                  .map((value) =>
                                    value.trim()
                                  )
                                  .filter(Boolean),
                            }
                          )
                        )
                      }
                    />

                    <span>
                      Separate multiple values with commas.
                    </span>

                  </div>
                )}

              </div>

            </SaaSSection>


            {/* =================================================
                INVENTORY / WAREHOUSE
            ================================================= */}

            <SaaSSection
              icon={
                productPayloads.assignedTo ===
                "WAREHOUSE_ITEM" ? (
                  <WarehouseOutlined />
                ) : (
                  <InventoryOutlined />
                )
              }
              eyebrow="STORAGE & FULFILLMENT"
              title="Inventory placement"
              description="Choose where this item is stored and configure its initial stock settings."
              badge={
                productPayloads.assignedTo
                  ? "Configured"
                  : "Required"
              }
            >

              <div className="uni-storage-type-grid">

                <button
                  type="button"
                  className={`uni-storage-option ${
                    productPayloads.assignedTo ===
                    "INVENTORY_ITEM"
                      ? "is-selected"
                      : ""
                  }`}
                  onClick={() => {
                    setProductPayloads({
                      assignedTo:
                        "INVENTORY_ITEM",
                      assignedToId: "",
                    });

                    setErrors((prev) => ({
                      ...prev,
                      assignedTo: "",
                      assignedToId: "",
                    }));
                  }}
                >
                  <div className="uni-storage-option__icon">
                    <StorefrontOutlined />
                  </div>

                  <div>
                    <strong>
                      Inventory location
                    </strong>

                    <span>
                      Track stock in a dedicated
                      inventory location.
                    </span>
                  </div>

                  <div className="uni-radio">
                    {productPayloads.assignedTo ===
                      "INVENTORY_ITEM" && (
                      <span />
                    )}
                  </div>
                </button>


                <button
                  type="button"
                  className={`uni-storage-option ${
                    productPayloads.assignedTo ===
                    "WAREHOUSE_ITEM"
                      ? "is-selected"
                      : ""
                  }`}
                  onClick={() => {
                    setProductPayloads({
                      assignedTo:
                        "WAREHOUSE_ITEM",
                      assignedToId: "",
                    });

                    setErrors((prev) => ({
                      ...prev,
                      assignedTo: "",
                      assignedToId: "",
                    }));
                  }}
                >
                  <div className="uni-storage-option__icon">
                    <WarehouseOutlined />
                  </div>

                  <div>
                    <strong>
                      Warehouse
                    </strong>

                    <span>
                      Store and manage stock from a
                      warehouse.
                    </span>
                  </div>

                  <div className="uni-radio">
                    {productPayloads.assignedTo ===
                      "WAREHOUSE_ITEM" && (
                      <span />
                    )}
                  </div>
                </button>

              </div>


              {productPayloads.assignedTo && (
                <div className="uni-storage-config">

                  <Field
                    label={
                      productPayloads.assignedTo ===
                      "INVENTORY_ITEM"
                        ? "Inventory location"
                        : "Warehouse"
                    }
                    required
                    error={errors.assignedToId}
                  >
                    <select
                      className={`ui-select ${
                        errors.assignedToId
                          ? "is-error"
                          : ""
                      }`}
                      value={
                        productPayloads.assignedToId
                      }
                      onChange={(e) => {
                        const selectedId =
                          e.target.value;

                        const selectedInventory =
                          inventoryData.find(
                            (item) =>
                              item.inventoryId ===
                              selectedId
                          );

                        const selectedWarehouse =
                          warehouseData.find(
                            (item) =>
                              item.warehouseId ===
                              selectedId
                          );

                        setProductPayloads({
                          assignedToId:
                            selectedId,

                          inventoryLocation:
                            selectedInventory?.location ||
                            selectedWarehouse?.location ||
                            "",
                        });

                        setErrors((prev) => ({
                          ...prev,
                          assignedToId: "",
                        }));
                      }}
                    >
                      <option value="">
                        Select{" "}
                        {productPayloads.assignedTo ===
                        "INVENTORY_ITEM"
                          ? "inventory"
                          : "warehouse"}
                      </option>

                      {productPayloads.assignedTo ===
                        "INVENTORY_ITEM" &&
                        inventoryData.map(
                          (inventory) => (
                            <option
                              key={
                                inventory._id
                              }
                              value={
                                inventory.inventoryId
                              }
                            >
                              {
                                inventory.inventoryName
                              }
                            </option>
                          )
                        )}

                      {productPayloads.assignedTo ===
                        "WAREHOUSE_ITEM" &&
                        warehouseData.map(
                          (warehouse) => (
                            <option
                              key={
                                warehouse._id
                              }
                              value={
                                warehouse.warehouseId
                              }
                            >
                              {
                                warehouse.warehouseName
                              }
                            </option>
                          )
                        )}
                    </select>
                  </Field>


                  <div className="uni-stock-grid">

                    <Field
                      label={
                        productPayloads.assignedTo ===
                        "INVENTORY_ITEM"
                          ? "Opening quantity"
                          : "Warehouse quantity"
                      }
                      required={
                        productPayloads.assignedTo ===
                        "INVENTORY_ITEM"
                      }
                      error={
                        errors.inventoryQuantity
                      }
                    >
                      <div className="ui-number-shell">
                        <InventoryOutlined />

                        <input
                          type="number"
                          min="0"
                          value={
                            productPayloads.assignedTo ===
                            "INVENTORY_ITEM"
                              ? productPayloads.inventoryQuantity ??
                                ""
                              : productPayloads.warehousesQuantity ??
                                ""
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value ===
                              ""
                                ? ""
                                : Number(
                                    e.target.value
                                  );

                            if (
                              productPayloads.assignedTo ===
                              "INVENTORY_ITEM"
                            ) {
                              setProductPayloads({
                                inventoryQuantity:
                                  value,
                              });
                            } else {
                              setProductPayloads({
                                warehousesQuantity:
                                  value,
                              });
                            }

                            setErrors((prev) => ({
                              ...prev,
                              inventoryQuantity:
                                "",
                            }));
                          }}
                        />

                        <span>
                          {productPayloads.unitsBaseUnit ||
                            "units"}
                        </span>
                      </div>
                    </Field>


                    <Field
                      label="Reserved quantity"
                      hint="Optional"
                    >
                      <div className="ui-number-shell">
                        <ConfirmationNumberOutlined />

                        <input
                          type="number"
                          min="0"
                          value={
                            productPayloads.assignedTo ===
                            "INVENTORY_ITEM"
                              ? productPayloads.inventoryReservedQuantity ??
                                ""
                              : productPayloads.warehousesReservedQuantity ??
                                ""
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value ===
                              ""
                                ? ""
                                : Number(
                                    e.target.value
                                  );

                            if (
                              productPayloads.assignedTo ===
                              "INVENTORY_ITEM"
                            ) {
                              setProductPayloads({
                                inventoryReservedQuantity:
                                  value,
                              });
                            } else {
                              setProductPayloads({
                                warehousesReservedQuantity:
                                  value,
                              });
                            }
                          }}
                        />
                      </div>
                    </Field>

                  </div>


                  <div className="uni-stock-rules">

                    <div className="uni-stock-rules__header">

                      <div>
                        <strong>
                          Replenishment rules
                        </strong>

                        <span>
                          Define when this item should
                          be reordered.
                        </span>
                      </div>

                      <SettingsOutlined />

                    </div>


                    <div className="uni-stock-grid">

                      <Field
                        label="Minimum level"
                        required
                        error={
                          errors.stockMinLevel
                        }
                      >
                        <div className="ui-number-shell">
                          <InventoryOutlined />

                          <input
                            type="number"
                            min="0"
                            value={
                              productPayloads.stockMinLevel ??
                              ""
                            }
                            onChange={(e) => {
                              setProductPayloads({
                                stockMinLevel:
                                  e.target.value ===
                                  ""
                                    ? ""
                                    : Number(
                                        e.target.value
                                      ),
                              });

                              setErrors((prev) => ({
                                ...prev,
                                stockMinLevel:
                                  "",
                              }));
                            }}
                          />
                        </div>
                      </Field>


                      <Field
                        label="Reorder level"
                        hint="Optional"
                      >
                        <div className="ui-number-shell">
                          <TrendingUp />

                          <input
                            type="number"
                            min="0"
                            value={
                              productPayloads.stockReorderLevel ??
                              ""
                            }
                            onChange={(e) =>
                              setProductPayloads({
                                stockReorderLevel:
                                  e.target.value ===
                                  ""
                                    ? ""
                                    : Number(
                                        e.target.value
                                      ),
                              })
                            }
                          />
                        </div>
                      </Field>


                      <Field
                        label="Reorder quantity"
                        hint="Optional"
                      >
                        <div className="ui-number-shell">
                          <Inventory2Outlined />

                          <input
                            type="number"
                            min="0"
                            value={
                              productPayloads.stockReorderQuantity ??
                              ""
                            }
                            onChange={(e) =>
                              setProductPayloads({
                                stockReorderQuantity:
                                  e.target.value ===
                                  ""
                                    ? ""
                                    : Number(
                                        e.target.value
                                      ),
                              })
                            }
                          />
                        </div>
                      </Field>

                    </div>

                  </div>

                </div>
              )}

            </SaaSSection>


            {/* =================================================
                VARIANTS
            ================================================= */}

            <SaaSSection
              icon={<FilterTiltShift />}
              eyebrow="PRODUCT CONFIGURATION"
              title="Variants"
              description="Create options such as size, color, packaging or other product variations."
              badge={
                ItemVariants?.length
                  ? `${ItemVariants.length} configured`
                  : "Optional"
              }
            >

              <div className="uni-variants-toolbar">

                <div>
                  <strong>
                    Variant options
                  </strong>

                  <span>
                    Add structured options to this item.
                  </span>
                </div>

                <button
                  type="button"
                  className="uni-outline-action"
                  onClick={() =>
                    setIsAddingVariant(
                      !isAddingVariant
                    )
                  }
                >
                  <BookmarkAddOutlined />
                  Add variant
                </button>

              </div>


              {isAddingVariant && (
                <div className="uni-variant-builder">

                  <div className="uni-builder-heading">
                    <div className="uni-builder-icon">
                      <AutoFixHighSharp />
                    </div>

                    <div>
                      <strong>
                        New variant option
                      </strong>

                      <span>
                        Define the option and its
                        available values.
                      </span>
                    </div>
                  </div>


                  <div className="uni-variant-form">

                    <Field
                      label="Option name"
                      required
                    >
                      <input
                        className="ui-plain-input"
                        type="text"
                        placeholder="e.g. Size"
                        value={
                          createVariantsPayloads?.option ||
                          ""
                        }
                        onChange={(e) =>
                          dispatch(
                            Action.setCreateVariantsPayloads(
                              {
                                name: "option",
                                value:
                                  e.target.value,
                              }
                            )
                          )
                        }
                      />
                    </Field>


                    <Field
                      label="Option values"
                      required
                      hint="Comma separated"
                    >
                      <input
                        className="ui-plain-input"
                        type="text"
                        placeholder="Small, Medium, Large"
                        value={
                          createVariantsPayloads?.values?.join(
                            ", "
                          ) || ""
                        }
                        onChange={(e) =>
                          dispatch(
                            Action.setCreateVariantsPayloads(
                              {
                                name: "values",
                                value:
                                  e.target.value
                                    .split(",")
                                    .map(
                                      (value) =>
                                        value.trim()
                                    ),
                              }
                            )
                          )
                        }
                      />
                    </Field>


                    <Field label="Image URL">
                      <input
                        className="ui-plain-input"
                        type="text"
                        placeholder="https://..."
                        value={
                          createVariantsPayloads?.imageUrl ||
                          ""
                        }
                        onChange={(e) =>
                          dispatch(
                            Action.setCreateVariantsPayloads(
                              {
                                name: "imageUrl",
                                value:
                                  e.target.value,
                              }
                            )
                          )
                        }
                      />
                    </Field>


                    <Field label="Available">
                      <input
                        className="ui-plain-input"
                        type="number"
                        min="0"
                        value={
                          createVariantsPayloads?.available ||
                          ""
                        }
                        onChange={(e) =>
                          dispatch(
                            Action.setCreateVariantsPayloads(
                              {
                                name: "available",
                                value:
                                  e.target.value,
                              }
                            )
                          )
                        }
                      />
                    </Field>


                    <Field label="Price">
                      <div className="ui-price-mini">
                        <span>₦</span>

                        <input
                          type="number"
                          min="0"
                          placeholder="0.00"
                          value={
                            createVariantsPayloads?.price ||
                            ""
                          }
                          onChange={(e) =>
                            dispatch(
                              Action.setCreateVariantsPayloads(
                                {
                                  name: "price",
                                  value:
                                    e.target.value,
                                }
                              )
                            )
                          }
                        />
                      </div>
                    </Field>

                  </div>


                  <div className="uni-builder-actions">

                    <button
                      type="button"
                      className="uni-builder-cancel"
                      onClick={() =>
                        setIsAddingVariant(false)
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="uni-builder-save"
                      onClick={addVariants}
                    >
                      <CheckCircle />
                      Save variant
                    </button>

                  </div>

                </div>
              )}


              {ItemVariants?.length > 0 ? (
                <div className="uni-variant-list">

                  {ItemVariants.map(
                    (variant, index) => (
                      <div
                        className="uni-variant-card"
                        key={index}
                      >

                        <div className="uni-variant-card__drag">
                          ⋮⋮
                        </div>

                        <div className="uni-variant-card__visual">
                          {variant.imageUrl ? (
                            <img
                              src={
                                variant.imageUrl
                              }
                              alt={
                                variant.option
                              }
                            />
                          ) : (
                            <FilterTiltShift />
                          )}
                        </div>

                        <div className="uni-variant-card__content">

                          <span>
                            OPTION
                          </span>

                          <strong>
                            {variant.option}
                          </strong>

                          <div>
                            {variant.values
                              ?.filter(Boolean)
                              .map(
                                (
                                  value,
                                  valueIndex
                                ) => (
                                  <span
                                    key={
                                      valueIndex
                                    }
                                  >
                                    {value}
                                  </span>
                                )
                              )}
                          </div>

                        </div>

                        <button
                          type="button"
                          className="uni-delete-variant"
                          onClick={() =>
                            dispatch(
                              Action.removeFromVariants(
                                index
                              )
                            )
                          }
                        >
                          <RemoveCircleOutline />
                        </button>

                      </div>
                    )
                  )}

                </div>
              ) : (
                <div className="uni-empty-variants">

                  <div>
                    <FilterTiltShift />
                  </div>

                  <strong>
                    No variants configured
                  </strong>

                  <span>
                    Add variants if this product has
                    different sizes, colors or packages.
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setIsAddingVariant(true)
                    }
                  >
                    Create first variant
                    <ArrowForward />
                  </button>

                </div>
              )}

            </SaaSSection>


            {/* =================================================
                BARCODE
            ================================================= */}

            <SaaSSection
              icon={<Numbers />}
              eyebrow="IDENTIFICATION"
              title="Barcode"
              description="Connect an existing barcode to make scanning and POS lookup faster."
              badge="Optional"
            >

              <Field
                label="Barcode number"
                hint="UPC, EAN or internal barcode"
              >
                <InputShell
                  icon={<Numbers />}
                >
                  <input
                    type="text"
                    value={
                      productPayloads.barcode
                    }
                    placeholder="Enter barcode number"
                    onChange={(e) =>
                      setProductPayloads({
                        barcode:
                          e.target.value,
                      })
                    }
                  />
                </InputShell>
              </Field>

            </SaaSSection>

          </div>


          {/* ===================================================
              SIDEBAR
          =================================================== */}

          <aside className="uni-sidebar">

            {/* ================================================
                STATUS CARD
            ================================================= */}

            <div className="uni-sidebar-card uni-sidebar-status">

              <div className="uni-sidebar-card__header">

                <div>
                  <span>
                    ITEM STATUS
                  </span>

                  <strong>
                    {statusLabel}
                  </strong>
                </div>

                <div className="uni-status-orb">
                  <CheckCircle />
                </div>

              </div>


              <div className="uni-health">

                <div className="uni-health__top">
                  <span>
                    Setup health
                  </span>

                  <strong>
                    {completion >= 80
                      ? "Excellent"
                      : completion >= 50
                        ? "Good"
                        : "Needs attention"}
                  </strong>
                </div>

                <div className="uni-health__track">
                  <span
                    style={{
                      width: `${completion}%`,
                    }}
                  />
                </div>

                <p>
                  {completion}% of the recommended
                  product profile is complete.
                </p>

              </div>

            </div>


            {/* ================================================
                ORGANIZATION
            ================================================= */}

            <div className="uni-sidebar-card">

              <div className="uni-sidebar-heading">

                <div className="uni-sidebar-heading__icon">
                  <StorefrontOutlined />
                </div>

                <div>
                  <span>
                    ORGANIZATION
                  </span>

                  <strong>
                    Catalog settings
                  </strong>
                </div>

              </div>


              <Field
                label="Item type"
                required
                error={errors.itemType}
              >
                <select
                  className={`ui-select ${
                    errors.itemType
                      ? "is-error"
                      : ""
                  }`}
                  value={
                    productPayloads.itemType
                  }
                  onChange={(e) => {
                    setProductPayloads({
                      itemType:
                        e.target.value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      itemType: "",
                    }));
                  }}
                >
                  <option value="">
                    Select item type
                  </option>

                  <option value="Product">
                    Product
                  </option>

                  <option value="Service">
                    Service
                  </option>

                  <option value="Production">
                    Production
                  </option>
                </select>
              </Field>


              <Field
                label="Status"
                required
                error={errors.status}
              >
                <select
                  className={`ui-select ${
                    errors.status
                      ? "is-error"
                      : ""
                  }`}
                  value={
                    productPayloads.status
                  }
                  onChange={(e) => {
                    setProductPayloads({
                      status:
                        e.target.value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      status: "",
                    }));
                  }}
                >
                  {productStatuses?.map(
                    (status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {String(status)
                          .replace(
                            /_/g,
                            " "
                          )
                          .replace(
                            /\b\w/g,
                            (char) =>
                              char.toUpperCase()
                          )}
                      </option>
                    )
                  )}
                </select>
              </Field>


              <div className="uni-sidebar-divider" />


              <div className="uni-unit-grid">

                <Field label="Base unit">
                  <select
                    className="ui-select"
                    value={
                      productPayloads.unitsBaseUnit
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        unitsBaseUnit:
                          e.target.value,
                      })
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {productUnits.map(
                      (unit) => (
                        <option
                          key={unit}
                          value={unit}
                        >
                          {unit}
                        </option>
                      )
                    )}
                  </select>
                </Field>


                <Field label="Purchase unit">
                  <select
                    className="ui-select"
                    value={
                      productPayloads.unitsPurchaseUnit
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        unitsPurchaseUnit:
                          e.target.value,
                      })
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {productUnits.map(
                      (unit) => (
                        <option
                          key={unit}
                          value={unit}
                        >
                          {unit}
                        </option>
                      )
                    )}
                  </select>
                </Field>


                <Field
                  label="Sales unit"
                  required
                  error={
                    errors.unitsSalesUnit
                  }
                >
                  <select
                    className={`ui-select ${
                      errors.unitsSalesUnit
                        ? "is-error"
                        : ""
                    }`}
                    value={
                      productPayloads.unitsSalesUnit
                    }
                    onChange={(e) => {
                      setProductPayloads({
                        unitsSalesUnit:
                          e.target.value,
                      });

                      setErrors((prev) => ({
                        ...prev,
                        unitsSalesUnit:
                          "",
                      }));
                    }}
                  >
                    <option value="">
                      Select
                    </option>

                    {productUnits.map(
                      (unit) => (
                        <option
                          key={unit}
                          value={unit}
                        >
                          {unit}
                        </option>
                      )
                    )}
                  </select>
                </Field>


                <Field label="Tax rate">
                  <div className="ui-percent-input">
                    <input
                      type="number"
                      min="0"
                      value={
                        productPayloads.pricingTaxRate ??
                        ""
                      }
                      placeholder="0"
                      onChange={(e) =>
                        setProductPayloads({
                          pricingTaxRate:
                            e.target.value ===
                            ""
                              ? ""
                              : Number(
                                  e.target.value
                                ),
                        })
                      }
                    />

                    <span>%</span>
                  </div>
                </Field>

              </div>

            </div>


            {/* ================================================
                QUICK METRICS
            ================================================= */}

            <div className="uni-sidebar-card">

              <div className="uni-sidebar-heading">

                <div className="uni-sidebar-heading__icon">
                  <TrendingUp />
                </div>

                <div>
                  <span>
                    LIVE METRICS
                  </span>

                  <strong>
                    Item snapshot
                  </strong>
                </div>

              </div>


              <div className="uni-metrics-grid">

                <MiniMetric
                  icon={<CurrencyExchangeOutlined />}
                  label="Selling"
                  value={`₦${sellingPrice.toLocaleString()}`}
                  tone="blue"
                />

                <MiniMetric
                  icon={<TrendingUp />}
                  label="Profit"
                  value={`₦${estimatedProfit.toLocaleString()}`}
                  tone="green"
                />

                <MiniMetric
                  icon={<InventoryOutlined />}
                  label="Variants"
                  value={
                    ItemVariants?.length || 0
                  }
                />

                <MiniMetric
                  icon={<PhotoLibraryOutlined />}
                  label="Images"
                  value={
                    productPayloads.images
                      ?.length || 0
                  }
                />

              </div>

            </div>


            {/* ================================================
                DIMENSIONS
            ================================================= */}

            <div className="uni-sidebar-card">

              <div className="uni-sidebar-heading">

                <div className="uni-sidebar-heading__icon">
                  <StraightenOutlined />
                </div>

                <div>
                  <span>
                    LOGISTICS
                  </span>

                  <strong>
                    Dimensions
                  </strong>
                </div>

              </div>


              <Field label="Measurement unit">
                <select
                  className="ui-select"
                  value={
                    productPayloads.unitsOfMeasure ||
                    ""
                  }
                  onChange={(e) =>
                    setProductPayloads({
                      unitsOfMeasure:
                        e.target.value,
                    })
                  }
                >
                  <option value="">
                    Select unit
                  </option>

                  {[
                    {
                      value: "MM",
                      label:
                        "Millimeter (mm)",
                    },
                    {
                      value: "CM",
                      label:
                        "Centimeter (cm)",
                    },
                    {
                      value: "M",
                      label:
                        "Meter (m)",
                    },
                    {
                      value: "IN",
                      label:
                        "Inch (in)",
                    },
                    {
                      value: "FT",
                      label:
                        "Foot (ft)",
                    },
                    {
                      value: "G",
                      label:
                        "Gram (g)",
                    },
                    {
                      value: "KG",
                      label:
                        "Kilogram (kg)",
                    },
                    {
                      value: "LB",
                      label:
                        "Pound (lb)",
                    },
                    {
                      value: "ML",
                      label:
                        "Milliliter (mL)",
                    },
                    {
                      value: "L",
                      label:
                        "Liter (L)",
                    },
                  ].map((unit) => (
                    <option
                      key={unit.value}
                      value={unit.value}
                    >
                      {unit.label}
                    </option>
                  ))}
                </select>
              </Field>


              <div className="uni-dimension-grid">

                <Field label="Length">
                  <input
                    className="ui-plain-input"
                    type="number"
                    value={
                      productPayloads.dimensionsLength ||
                      ""
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        dimensionsLength:
                          e.target.value,
                      })
                    }
                  />
                </Field>

                <Field label="Width">
                  <input
                    className="ui-plain-input"
                    type="number"
                    value={
                      productPayloads.dimensionsWidth ||
                      ""
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        dimensionsWidth:
                          e.target.value,
                      })
                    }
                  />
                </Field>

                <Field label="Height">
                  <input
                    className="ui-plain-input"
                    type="number"
                    value={
                      productPayloads.dimensionsHeight ||
                      ""
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        dimensionsHeight:
                          e.target.value,
                      })
                    }
                  />
                </Field>

                <Field label="Weight">
                  <input
                    className="ui-plain-input"
                    type="number"
                    value={
                      productPayloads.dimensionsWeight ||
                      ""
                    }
                    onChange={(e) =>
                      setProductPayloads({
                        dimensionsWeight:
                          e.target.value,
                      })
                    }
                  />
                </Field>

              </div>

            </div>


            {/* ================================================
                CHANNELS
            ================================================= */}

            <div className="uni-sidebar-card">

              <div className="uni-sidebar-heading">

                <div className="uni-sidebar-heading__icon">
                  <LocalShippingOutlined />
                </div>

                <div>
                  <span>
                    DISTRIBUTION
                  </span>

                  <strong>
                    Publishing
                  </strong>
                </div>

              </div>


              <div className="uni-channel-card">

                <div className="uni-channel-icon">
                  <StorefrontOutlined />
                </div>

                <div>
                  <strong>
                    All channels
                  </strong>

                  <span>
                    Available across connected
                    sales channels.
                  </span>
                </div>

                <CheckCircle />

              </div>

            </div>


            {/* ================================================
                LIVE PREVIEW
            ================================================= */}

            <div className="uni-sidebar-card uni-preview-card">

              <div className="uni-preview-header">

                <div>
                  <span>
                    LIVE PREVIEW
                  </span>

                  <strong>
                    Product card
                  </strong>
                </div>

                <VisibilityOutlined />

              </div>


              <div className="uni-preview-product">

                <div className="uni-preview-image">

                  {productPayloads.images?.[0] ? (
                    <img
                      src={
                        productPayloads.images[0]
                      }
                      alt={
                        productPayloads.name ||
                        "Product"
                      }
                    />
                  ) : (
                    <Inventory2Outlined />
                  )}

                </div>


                <div className="uni-preview-body">

                  <span>
                    {productPayloads.itemType ||
                      "PRODUCT"}
                  </span>

                  <h3>
                    {productPayloads.name ||
                      "Your item name"}
                  </h3>

                  <p>
                    {productPayloads.brand ||
                      "Brand name"}
                  </p>

                  <div className="uni-preview-price">
                    <strong>
                      ₦
                      {sellingPrice.toLocaleString()}
                    </strong>

                    <small>
                      /{" "}
                      {productPayloads.unitsSalesUnit ||
                        "unit"}
                    </small>
                  </div>

                </div>

              </div>

            </div>


            {/* ================================================
                HIGHLIGHTS
            ================================================= */}

            <div className="uni-sidebar-card">

              <div className="uni-sidebar-heading">

                <div className="uni-sidebar-heading__icon">
                  <AutoFixHighSharp />
                </div>

                <div>
                  <span>
                    INTELLIGENCE
                  </span>

                  <strong>
                    Item highlights
                  </strong>
                </div>

              </div>


              <div className="uni-highlight-list">

                <div>
                  <CheckCircle />

                  <span>
                    Profit
                  </span>

                  <strong>
                    ₦
                    {estimatedProfit.toLocaleString()}
                  </strong>
                </div>


                <div>
                  <CheckCircle />

                  <span>
                    Margin
                  </span>

                  <strong>
                    {margin.toFixed(1)}%
                  </strong>
                </div>


                <div>
                  <CheckCircle />

                  <span>
                    Storage
                  </span>

                  <strong>
                    {productPayloads.assignedTo
                      ? productPayloads.assignedTo ===
                        "INVENTORY_ITEM"
                        ? "Inventory"
                        : "Warehouse"
                      : "Not set"}
                  </strong>
                </div>

              </div>

            </div>

          </aside>

        </main>


        {/* =====================================================
            STICKY FOOTER
        ===================================================== */}

        <footer className="uni-create-footer">

          <div className="uni-footer-status">

            <div className="uni-footer-status__icon">
              <CheckCircle />
            </div>

            <div>
              <strong>
                {completion >= 100
                  ? "Item is ready to save"
                  : "Complete your item profile"}
              </strong>

              <span>
                {completion >= 100
                  ? "All recommended fields are complete."
                  : `${8 -
                      completionFields.filter(Boolean)
                        .length} required or recommended fields remaining.`}
              </span>
            </div>

          </div>


          <div className="uni-footer-actions">

            <button
              type="button"
              className="uni-footer-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>

            <button
              type="button"
              className="uni-footer-save"
              onClick={handleAddNewItem}
            >
              <Save />
              Save item
              <ArrowForward />
            </button>

          </div>

        </footer>

      </div>
    </>
  );
}